import React, { useState, useEffect } from 'react';
import {
  UserPlus,
  Calendar,
  Clock,
  AlertCircle,
  CheckCircle,
  XCircle,
  Save,
  FileText,
  Users,
  Settings,
  Info
} from 'lucide-react';
import { schoolDataApi } from '../../services/api';
import { sortClasses } from '../../utils/classUtils';

interface RegistrationConfig {
  id?: string;
  isRegistrationOpen: boolean;
  registrationStartDate: string;
  registrationEndDate: string;
  maxRegistrations?: number;
  allowedClasses: string[];
  registrationMessage: string;
  requireApproval: boolean;
  autoAssignStudentId: boolean;
  notificationEmail: string;
  createdAt?: string;
  updatedAt?: string;
}

const RegistrationSettings: React.FC = () => {
  const [config, setConfig] = useState<RegistrationConfig>({
    isRegistrationOpen: false,
    registrationStartDate: '',
    registrationEndDate: '',
    maxRegistrations: undefined,
    allowedClasses: [],
    registrationMessage: 'Registration is currently open for the academic year. Please fill out the form completely.',
    requireApproval: true,
    autoAssignStudentId: true,
    notificationEmail: ''
  });

  const [classes, setClasses] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<string>('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load classes
      const classesData = await schoolDataApi.classes.getAll();
      setClasses(sortClasses(classesData.filter(cls => cls.isActive)));

      // Load registration settings (this would need to be implemented in the API)
      // For now, we'll use default settings
      // const registrationData = await schoolDataApi.getRegistrationSettings();

    } catch (error) {
      console.error('Error loading registration settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);

      // Save registration settings (this would need to be implemented in the API)
      // await schoolDataApi.updateRegistrationSettings(config);

      setLastSaved(new Date().toLocaleString());
      console.log('Registration settings saved:', config);

    } catch (error) {
      console.error('Error saving registration settings:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleClassToggle = (className: string) => {
    setConfig(prev => ({
      ...prev,
      allowedClasses: prev.allowedClasses.includes(className)
        ? prev.allowedClasses.filter(cls => cls !== className)
        : [...prev.allowedClasses, className]
    }));
  };

  const getCurrentStatus = () => {
    const now = new Date();
    const startDate = new Date(config.registrationStartDate);
    const endDate = new Date(config.registrationEndDate);

    if (!config.isRegistrationOpen) {
      return { status: 'closed', message: 'Registration is currently closed', color: 'text-[#f38b83]' };
    }

    if (config.registrationStartDate && now < startDate) {
      return { status: 'scheduled', message: `Registration opens on ${startDate.toLocaleDateString()}`, color: 'text-[#f0b64c]' };
    }

    if (config.registrationEndDate && now > endDate) {
      return { status: 'expired', message: `Registration closed on ${endDate.toLocaleDateString()}`, color: 'text-[#f38b83]' };
    }

    return { status: 'open', message: 'Registration is currently open', color: 'text-[#4ade80]' };
  };

  const status = getCurrentStatus();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#e2916d] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Current Status */}
      <div className="bg-[#28221a] border border-[#3a3122] rounded-lg p-4">
        <div className="flex items-center space-x-3">
          <div className={`w-3 h-3 rounded-full ${
            status.status === 'open' ? 'bg-[#4ade80]' :
            status.status === 'scheduled' ? 'bg-[#f0b64c]' :
            'bg-[#f38b83]'
          }`}></div>
          <div>
            <h3 className="font-medium text-[#f3ede2]">Registration Status</h3>
            <p className={`text-sm ${status.color}`}>{status.message}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* General Settings */}
        <div className="space-y-6">
          <div className="bg-[#211c15] border border-[#3a3122] rounded-lg p-6">
            <h3 className="text-lg font-semibold text-[#f3ede2] mb-4 flex items-center">
              <Settings className="w-5 h-5 mr-2 text-[#e2916d]" />
              General Settings
            </h3>

            <div className="space-y-4">
              {/* Registration Toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-[#b9ad9b]">Enable Registration</label>
                  <p className="text-xs text-[#8a7f6e]">Allow new student registrations</p>
                </div>
                <button
                  onClick={() => setConfig(prev => ({ ...prev, isRegistrationOpen: !prev.isRegistrationOpen }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    config.isRegistrationOpen ? 'bg-[#e2916d]' : 'bg-[#4a4030]'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      config.isRegistrationOpen ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* Registration Dates */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                    Start Date
                  </label>
                  <input
                    type="datetime-local"
                    value={config.registrationStartDate}
                    onChange={(e) => setConfig(prev => ({ ...prev, registrationStartDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                    End Date
                  </label>
                  <input
                    type="datetime-local"
                    value={config.registrationEndDate}
                    onChange={(e) => setConfig(prev => ({ ...prev, registrationEndDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
                  />
                </div>
              </div>

              {/* Max Registrations */}
              <div>
                <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                  Maximum Registrations (Optional)
                </label>
                <input
                  type="number"
                  min="1"
                  value={config.maxRegistrations || ''}
                  onChange={(e) => setConfig(prev => ({ ...prev, maxRegistrations: e.target.value ? parseInt(e.target.value) : undefined }))}
                  className="w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
                  placeholder="Leave empty for unlimited"
                />
              </div>

              {/* Notification Email */}
              <div>
                <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                  Notification Email
                </label>
                <input
                  type="email"
                  value={config.notificationEmail}
                  onChange={(e) => setConfig(prev => ({ ...prev, notificationEmail: e.target.value }))}
                  className="w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
                  placeholder="admin@school.com"
                />
                <p className="text-xs text-[#8a7f6e] mt-1">Email to receive registration notifications</p>
              </div>
            </div>
          </div>

          {/* Options */}
          <div className="bg-[#211c15] border border-[#3a3122] rounded-lg p-6">
            <h3 className="text-lg font-semibold text-[#f3ede2] mb-4 flex items-center">
              <CheckCircle className="w-5 h-5 mr-2 text-[#e2916d]" />
              Registration Options
            </h3>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-[#b9ad9b]">Require Approval</label>
                  <p className="text-xs text-[#8a7f6e]">All registrations need admin approval</p>
                </div>
                <button
                  onClick={() => setConfig(prev => ({ ...prev, requireApproval: !prev.requireApproval }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    config.requireApproval ? 'bg-[#e2916d]' : 'bg-[#4a4030]'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      config.requireApproval ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-[#b9ad9b]">Auto-assign Student ID</label>
                  <p className="text-xs text-[#8a7f6e]">Automatically generate student IDs</p>
                </div>
                <button
                  onClick={() => setConfig(prev => ({ ...prev, autoAssignStudentId: !prev.autoAssignStudentId }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    config.autoAssignStudentId ? 'bg-[#e2916d]' : 'bg-[#4a4030]'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      config.autoAssignStudentId ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Allowed Classes & Message */}
        <div className="space-y-6">
          {/* Allowed Classes */}
          <div className="bg-[#211c15] border border-[#3a3122] rounded-lg p-6">
            <h3 className="text-lg font-semibold text-[#f3ede2] mb-4 flex items-center">
              <Users className="w-5 h-5 mr-2 text-[#e2916d]" />
              Allowed Classes
            </h3>

            <div className="space-y-2 max-h-60 overflow-y-auto">
              {classes.length === 0 ? (
                <p className="text-[#8a7f6e] text-center py-4">No classes available</p>
              ) : (
                classes.map((cls) => (
                  <label key={cls.id} className="flex items-center space-x-3 p-2 hover:bg-[#28221a] rounded-lg cursor-pointer">
                    <input
                      type="checkbox"
                      checked={config.allowedClasses.includes(cls.name)}
                      onChange={() => handleClassToggle(cls.name)}
                      className="w-4 h-4 text-[#e2916d] focus:ring-[#e2916d] border-[#4a4030] rounded"
                    />
                    <div className="flex-1">
                      <span className="text-[#f3ede2] font-medium">{cls.name}</span>
                      <span className="text-[#8a7f6e] text-sm ml-2">
                        ({cls.sections.length} sections: {cls.sections.join(', ')})
                      </span>
                    </div>
                  </label>
                ))
              )}
            </div>

            {config.allowedClasses.length === 0 && (
              <div className="mt-4 p-3 bg-[#332711] border border-[#4a3a19] rounded-lg">
                <div className="flex items-center space-x-2">
                  <AlertCircle className="w-4 h-4 text-[#f0b64c]" />
                  <span className="text-[#f5c876] text-sm">
                    No classes selected. Registration will be unavailable.
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Registration Message */}
          <div className="bg-[#211c15] border border-[#3a3122] rounded-lg p-6">
            <h3 className="text-lg font-semibold text-[#f3ede2] mb-4 flex items-center">
              <FileText className="w-5 h-5 mr-2 text-[#e2916d]" />
              Registration Message
            </h3>

            <div>
              <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                Public Message
              </label>
              <textarea
                value={config.registrationMessage}
                onChange={(e) => setConfig(prev => ({ ...prev, registrationMessage: e.target.value }))}
                rows={4}
                className="w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
                placeholder="Enter a message that will be displayed on the registration page..."
              />
              <p className="text-xs text-[#8a7f6e] mt-1">
                This message will be shown to parents/students on the registration page
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-[#8a7f6e]">
          {lastSaved && `Last saved: ${lastSaved}`}
        </div>
        <button
          onClick={handleSaveSettings}
          disabled={saving}
          className="px-6 py-2 bg-[#e2916d] hover:bg-[#f0a67d] disabled:bg-[#8a7f6e] text-white rounded-lg transition-colors flex items-center space-x-2"
        >
          {saving && (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          )}
          <Save className="w-4 h-4" />
          <span>{saving ? 'Saving...' : 'Save Settings'}</span>
        </button>
      </div>

      {/* Information */}
      <div className="bg-[#28221a] border border-[#3a3122] rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Info className="w-5 h-5 text-[#e2916d] mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-[#f3ede2] mb-1">Important Notes</h4>
            <ul className="text-sm text-[#8a7f6e] space-y-1">
              <li>• Registration settings apply to public registration forms</li>
              <li>• Admin can always add students manually regardless of these settings</li>
              <li>• Changes take effect immediately after saving</li>
              <li>• Email notifications will be sent to the specified notification email</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrationSettings;